import { makeAutoObservable } from "mobx";
import { PurchaseOrderUnavailabilityDetails, } from "../ordering";
import { PPrice } from "./PPrice";
import { POrderLine } from "./POrderLine";
import { formatFulfillmentMethodType } from "./utils";
export class PPurchaseOrder {
    constructor(params) {
        this.domain = params.domain;
        this.restaurant = params.restaurant;
        this.localizationProvider = params.localizationProvider;
        makeAutoObservable(this);
    }
    get availability() {
        return this.domain.availability;
    }
    get productTypeIds() {
        return this.domain.payload.productTypeIds;
    }
    get isUnavailableDueToPreorderConflict() {
        return this.domain.orderLines.some((orderLine) => {
            return orderLine.productInstance.availability.unavailabilityReasons.includes("Preorder");
        });
    }
    get error() {
        const unavailabilityDetails = new PurchaseOrderUnavailabilityDetails({
            localizationProvider: this.localizationProvider,
            purchaseOrder: this.domain,
            restaurant: this.restaurant,
        });
        return unavailabilityDetails.error;
    }
    get pPrice() {
        return new PPrice(this.localizationProvider, this.domain.price);
    }
    get price() {
        return this.pPrice.mainPriceValue;
    }
    get alternativePrice() {
        return this.pPrice.alternativePriceValue;
    }
    get grandTotal() {
        return this.pPrice.grandPriceValue;
    }
    get finalPrice() {
        return this.domain.price.finalValue;
    }
    get validationPrice() {
        return this.domain.validationPrice;
    }
    get packagingPrice() {
        const packagingPrice = this.domain.packagingPrice;
        if (packagingPrice.isZero) {
            return null;
        }
        return this.localizationProvider.formatDualPrice(packagingPrice);
    }
    get deliveryPrice() {
        const deliveryPrice = this.domain.deliveryPrice;
        if (deliveryPrice.isZero) {
            return null;
        }
        return this.localizationProvider.formatDualPrice(deliveryPrice);
    }
    get orderLines() {
        return this.domain.orderLines.map((orderLine) => new POrderLine({
            domain: orderLine,
            restaurant: this.restaurant,
            localizedPrice: new PPrice(this.localizationProvider, orderLine.totalPrice),
        })).raw;
    }
    get canPlaceOrder() {
        return this.domain.canPlaceOrder;
    }
    get isReadyForCheckout() {
        return this.domain.isReadyForCheckout;
    }
    get isCheckoutFormValid() {
        return this.domain.isCheckoutFormValid;
    }
    get isAgreedToMarketingConsents() {
        return (this.domain.consents.subscribeEmail || this.domain.consents.subscribePhone);
    }
    addOrderLine(orderLine) {
        this.domain.addOrderLine(orderLine);
    }
    get numItems() {
        return this.domain.numberOfItems.value;
    }
    clear() {
        this.domain.clear();
    }
    get contactDetailsLocked() {
        return this.domain.contactDetails.locked;
    }
    unlockContactDetails() {
        this.domain.contactDetails.unlock();
    }
    // NOTICE: NoContactDelivery method is not served as an option but as a checkbox inside "Delivery" form
    get fulfillmentMethodOptions() {
        const { fulfillmentMethods } = this.restaurant.domain;
        return fulfillmentMethods !== null
            ? fulfillmentMethods.raw
                .filter((method) => method !== "NoContactDelivery")
                .map((method) => ({
                value: method,
                label: formatFulfillmentMethodType(method),
            }))
            : [];
    }
    get paymentMedium() {
        return this.domain.fulfillmentInstructions.paymentMedium;
    }
    checkoutData(params) {
        return this.domain.checkoutData(params);
    }
    get instanceCounts() {
        return this.domain.orderLines.map((line) => ({
            instance: line.productInstance,
            quantity: line.quantity.value,
        }));
    }
    get isPaymentMediumRestored() {
        return this.domain.fulfillmentInstructions.paymentMediumRestored;
    }
    get menuOrderOrigin() {
        return this.domain.origin.menuOrderOrigin;
    }
}
